import _ from 'lodash';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import CardService from '../../services/card.service';
import FinanceService, { CanAffordPurchase } from '../../services/finance.service';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';

// components
import { FieldControl, Label } from '@americanexpress/dls-react';
import Anchor from '../atomic/atoms/anchor/Anchor';
import BWRadioGroup from '../bw-radio-group/BWRadioGroup';
import BwSlider from '../bw-slider/BwSlider';
import DrawButton from 'src/bw/draw-button/DrawButton';
import EditableField from 'src/bw/editable-field/EditableField';
import GoalDrawer from './GoalDrawer';
import InfoCallout from '../callout/InfoCallout';
import Output from '../atomic/molecules/output/Output';
import SavingsGoal from 'src/bw/bw-drawer/common/SavingsGoal';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import { ReactComponent as GraduationCapIcon } from 'src/bw/goal/graduation-cap.svg';
import { COLLEGE_FUNDING_PICKER, HELP_DOMAIN } from 'src/utils/constants';

interface PlanForEducationInterface {
    showCustomEducationCost: boolean;
}

export default class PlanForEducationCostsDrawer extends Component<any, PlanForEducationInterface> {
    public override state = {
        showCustomEducationCost: false
    };

    public override render(): ReactComponentElement<any> {
        let isPublic: boolean = true;
        let inState: boolean = true;
        let financialAid: number = 0;
        let roomBoard: number = 0;
        let customCost: number = 0 as number;

        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = _.get(drawerProps, 'card');
        const attributes: any = _.get(card, 'attributes');

        const totalMonths = card.startDate.until(card.endDate, ChronoUnit.MONTHS) + 1;
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        const totalAmount: number = this.calculateTotalAmount(card, attributes);
        let timeString = '';

        switch (years) {
            case 0:
                break;
            case 1:
                timeString = `${years} year`;
                break;
            default:
                timeString = `${years} years`;
                break;
        }
        if (months > 0) {
            timeString += ` and ${months} ${months === 1 ? 'month' : 'months'}`;
        }

        if (attributes) {
            isPublic = attributes.isPublic || isPublic;
            inState = attributes.inState || inState;
            financialAid = attributes.financialAid || financialAid;
            roomBoard = attributes.roomBoard || roomBoard;
            customCost = attributes.customCost || customCost;
        }

        let costString: string = LiteralsService.getLiteral('PlanForEducationCostsDrawer.totalCost', [timeString]);
        if (customCost) {
            costString += ' based on your education costs';
        } else {
            if (roomBoard > 0) {
                costString += ' with room, board, and books';
            }

            if (financialAid > 0) {
                costString += ' after financial aid';
            }
        }

        const radios: JSX.Element = (
            <>
                <BWRadioGroup
                    label="Public or Private School"
                    radioOptions={[
                        { id: 'public', value: 'true', label: 'Public' },
                        { id: 'private', value: 'false', label: 'Private' }
                    ]}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onChange={async (e: any) => {
                        await this.changeAttributeAndUpdateTotal({
                            card,
                            attributes,
                            attributeName: 'isPublic',
                            attributeValue: e.target.value
                        });
                        await this.handleUpdateSavingsGoal({
                            card,
                            attributes
                        });
                    }}
                    defaultChecked={isPublic.toString()}
                />

                <BWRadioGroup
                    label="In-state or out-of-state tuition"
                    radioOptions={[
                        {
                            id: 'in-state',
                            value: 'true',
                            label: 'In-state',
                            disabled: isPublic.toString() === 'false'
                        },
                        {
                            id: 'out-of-state',
                            value: 'false',
                            label: 'Out-of-state',
                            disabled: isPublic.toString() === 'false'
                        }
                    ]}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onChange={async (e: any) => {
                        await this.changeAttributeAndUpdateTotal({
                            card,
                            attributes,
                            attributeName: 'inState',
                            attributeValue: e.target.value
                        });
                        await this.handleUpdateSavingsGoal({
                            card,
                            attributes
                        });
                    }}
                    defaultChecked={inState.toString()}
                />
            </>
        );

        const sliders: JSX.Element = (
            <>
                <BwSlider
                    id="aid-slider"
                    label="Total financial aid"
                    value={financialAid}
                    formattedValue={FormatService.formatCompactMoney(financialAid)}
                    step={1000}
                    min={0}
                    max={400000}
                    onChange={async (value: any) =>
                        await this.changeAttributeAndUpdateTotal({
                            card,
                            attributes,
                            attributeName: 'financialAid',
                            attributeValue: value
                        })
                    }
                    onDone={async (value: any) =>
                        await this.handleUpdateSavingsGoal({
                            card,
                            attributes
                        })
                    }
                    inputToggleText="Add a precise amount"
                    hintText={LiteralsService.getLiteral('PlanForEducationCostsDrawer.amountForTime', [timeString])}
                />

                <div className="drawerSection">
                    <BwSlider
                        id="room-board-slider"
                        label="Total room, board and book costs"
                        value={roomBoard}
                        formattedValue={FormatService.formatCompactMoney(roomBoard)}
                        step={1000}
                        min={0}
                        max={100000}
                        onChange={async (value: any) =>
                            await this.changeAttributeAndUpdateTotal({
                                card,
                                attributes,
                                attributeName: 'roomBoard',
                                attributeValue: value
                            })
                        }
                        onDone={async (value: any) =>
                            await this.handleUpdateSavingsGoal({
                                card,
                                attributes
                            })
                        }
                        inputToggleText="Add a precise amount"
                        hintText={LiteralsService.getLiteral('PlanForEducationCostsDrawer.amountForTime', [timeString])}
                    />

                    <InfoCallout noMargin text="The average cost for room, board, and books is $12,414 per year." />
                </div>
            </>
        );

        const addCustomCostLink: JSX.Element = (
            <div>
                <Typography variant={TypographyVariants.BODY_BOLD} component={'div'} style={{ marginBottom: '8px' }}>
                    Have a specific education cost to enter?
                </Typography>
                <Anchor
                    href="#"
                    onClick={() => {
                        this.setState({
                            showCustomEducationCost: true
                        });
                        this.props.handleChangeCardAttribute(card, 150000, 'customCost');
                    }}
                    theme={{ display: 'block' }}
                >
                    Enter a fixed education cost
                </Anchor>
            </div>
        );

        const customCostField: JSX.Element = (
            <FieldControl>
                <Label>Education cost</Label>
                <EditableField
                    id="custom-cost"
                    value={FormatService.format(customCost)}
                    editValue={Number(customCost)}
                    handleSave={async (value: any) => {
                        await this.changeAttributeAndUpdateTotal({
                            card,
                            attributes,
                            attributeName: 'customCost',
                            attributeValue: value
                        });
                        await this.handleUpdateSavingsGoal({
                            card,
                            attributes
                        });
                    }}
                    currencyAdornment="$"
                    editing={true}
                    fullWidth
                />
            </FieldControl>
        );

        const removeCustomCostLink: JSX.Element = (
            <Anchor
                href="#"
                onClick={async () => {
                    await this.changeAttributeAndUpdateTotal({
                        card,
                        attributes,
                        attributeName: 'customCost',
                        attributeValue: 0
                    });
                    await this.handleUpdateSavingsGoal({
                        card,
                        attributes
                    });
                    this.setState({ showCustomEducationCost: false });
                }}
            >
                Switch back to slider controls
            </Anchor>
        );

        const helpUrl: string = HELP_DOMAIN ? HELP_DOMAIN + '/articles/plan-for-education' : null;

        const canAfford: CanAffordPurchase = FinanceService.canAffordEducation(
            card.startDate,
            years,
            Math.abs(totalAmount / years),
            (card as any).monthlySavings,
            (card as any).monthlyFreeCash
        );

        const drawButton: JSX.Element =
            COLLEGE_FUNDING_PICKER && card.fundingSources && card.fundingSources.length ? (
                <DrawButton
                    card={card}
                    callback={(investmentIds: number[]) => {
                        this.props.handleUpdateCard({ ...card, investmentIds }, null, true);
                    }}
                />
            ) : null;

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.props.handleUpdateCard}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<GraduationCapIcon />}
                introContent={LiteralsService.getLiteral('PlanForEducationCostsDrawer.intro')}
                learnMoreContent="Learn about how we calculate education costs"
                learnMoreUrl={helpUrl}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                startDateLabel="Planned education start date"
                hasEndDate
                endDateLabel="Planned education end date"
            >
                <Output
                    title="Expected education costs"
                    main={this.getDisplayAmount(customCost, totalAmount)}
                    subtext={`${costString}.`}
                    canAfford={canAfford}
                />
                {this.state.showCustomEducationCost || customCost !== 0 ? (
                    <div className="drawerSection">
                        {customCostField}
                        {removeCustomCostLink}
                    </div>
                ) : (
                    <>
                        {radios}
                        {sliders}
                        {addCustomCostLink}
                    </>
                )}

                <SavingsGoal
                    drawerProps={drawerProps}
                    user={this.props.user}
                    setDrawer={this.props.setDrawer}
                    handleOpenSignup={() => {
                        console.warn('Deprecated: handleOpenSignup (Plan For Education)');
                        this.props.handleOpenSignup();
                    }}
                    handleCreateLinkedSavingsCard={this.props.handleCreateLinkedSavingsCard}
                    handleChangeCardAttribute={this.props.handleChangeCardAttribute}
                    handleUpdateCard={this.props.handleUpdateCard}
                    handleDeleteCard={this.props.handleDeleteCard}
                />

                {drawButton}
            </GoalDrawer>
        );
    }

    private calculateTotalAmount(card: CardModel, attributes: any): number {
        const thisYear: number = LocalDate.now().year();
        if (attributes.customCost) return attributes.customCost * -1;
        const daysBetween = card.startDate.until(card.endDate, ChronoUnit.DAYS);
        let firstDayCostIn2020: number = 0;
        let firstDayCost: number = 0;
        let apr: number = 0;
        let dailyPercentage: number = 0;
        const isPublic: string = attributes.isPublic.toString();
        const inState: string = attributes.inState.toString();

        // The radio buttons don't work properly with boolean values, so we use strings
        switch (true) {
            case isPublic === 'true' && inState === 'true':
                apr = 0.007;
                firstDayCostIn2020 = 26.24657534246575;
                break;
            case isPublic === 'true' && inState === 'false':
                firstDayCostIn2020 = 75.16986301369863;
                apr = 0.005;
                break;
            case isPublic === 'false':
                firstDayCostIn2020 = 75.16986301369863;
                apr = 0.015;
                break;
        }

        // account for increase in costs since our current numbers
        firstDayCost = firstDayCostIn2020 * (1 + apr * (card.startDate.year() - thisYear));
        // get daily increase instead of annual
        dailyPercentage = apr / 365;
        // make an array with the cost of every individual day during the course of education
        const costs: number[] = Array.from(
            { length: daysBetween },
            (v: number, k: number) => firstDayCost * (1 + dailyPercentage * k)
        );
        // sum every day of education
        let totalAmount = _.sum(costs);
        // add user-defined costs (not affected by inflation)
        totalAmount += Number(attributes.roomBoard) || 0;
        totalAmount -= Number(attributes.financialAid) || 0;

        if (totalAmount <= 0) return 0;
        return totalAmount * -1;
    }

    private async changeAttributeAndUpdateTotal(args: {
        card: CardModel;
        attributes: any;
        attributeName: string;
        attributeValue: any;
    }): Promise<void> {
        args.attributes[args.attributeName] = args.attributeValue;
        args.attributes.totalAmount = this.calculateTotalAmount(args.card, args.attributes);

        await this.props.handleChangeCardAttributes(args.card, args.attributes);
    }

    private async handleUpdateSavingsGoal(args: { card: CardModel; attributes: any }): Promise<void> {
        if (CardService.hasSavingsGoal(this.props)) {
            const updatedCard: CardModel = CardService.getSavingsGoal(this.props);
            _.set(
                updatedCard,
                'attributes.targetBalance',
                Math.abs(this.calculateTotalAmount(args.card, args.attributes))
            );
            await this.props.handleUpdateCard(updatedCard);
        }
    }

    private getDisplayAmount(customCost: number, totalAmount: number): string {
        switch (true) {
            case Boolean(customCost):
                return FormatService.format(Math.abs(customCost));
            case Boolean(totalAmount):
                return FormatService.format(Math.abs(totalAmount));
            default:
                return FormatService.format(0);
        }
    }
}
